import { Facebook, Instagram, Linkedin, Mail, Phone } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/lib/i18n";
import { CONTACTS } from "@/components/landing/SeoSchema";

const NAV = [
  { label: "Hem", href: "#top", testid: "footer-link-hem" },
  { label: "Tjänster", href: "#tjanster", testid: "footer-link-tjanster" },
  { label: "Process", href: "#process", testid: "footer-link-process" },
  { label: "Projekt", href: "#projekt", testid: "footer-link-projekt" },
  { label: "Priser", href: "#paket", testid: "footer-link-priser" },
  { label: "Kontakt", href: "#kontakt", testid: "footer-link-kontakt" },
];

export default function Footer({ socials = {} }) {
  const { t } = useLang();
  const socialLinks = [
    { icon: Instagram, label: "Instagram", url: socials?.instagram, testid: "social-instagram" },
    { icon: Facebook, label: "Facebook", url: socials?.facebook, testid: "social-facebook" },
    { icon: Linkedin, label: "LinkedIn", url: socials?.linkedin, testid: "social-linkedin" },
  ];
  const navKeys = ["nav.home", "nav.tjanster", "nav.process", "nav.projekt", "nav.priser", "nav.kontakt"];
  return (
    <footer id="kontakt" data-testid="footer" className="border-t border-white/10 bg-coal">
      <div className="mx-auto max-w-7xl px-4 pt-20 sm:px-6 lg:px-8">
        <div className="select-none overflow-hidden" aria-hidden="true">
          <p className="whitespace-nowrap font-display text-[16vw] font-black leading-none tracking-tight text-white/5 lg:text-[11rem]">
            NIVÅ DIGITAL
          </p>
        </div>
        <div className="grid gap-12 border-t border-white/10 py-14 md:grid-cols-3">
          <div>
            <p className="flex items-baseline gap-1 font-display text-xl font-extrabold tracking-tight text-bone">
              NIVÅ<span className="inline-block h-2 w-2 rounded-full bg-mint" />
            </p>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-fog">
              {t("footer.tagline")}
            </p>
            <div className="mt-6 flex gap-3">
              {socialLinks.map((s) =>
                s.url ? (
                  <a
                    key={s.label}
                    data-testid={s.testid}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-fog transition-colors duration-300 hover:border-mint hover:text-mint"
                  >
                    <s.icon className="h-4 w-4" />
                  </a>
                ) : (
                  <span
                    key={s.label}
                    data-testid={s.testid}
                    title="Lägg till länk via adminpanelen"
                    aria-label={`${s.label} (ingen länk ännu)`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-fog/40"
                  >
                    <s.icon className="h-4 w-4" />
                  </span>
                ),
              )}
            </div>
          </div>
          <nav aria-label="Sidfot navigation">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-fog">{t("footer.menu")}</p>
            <ul className="mt-5 space-y-3">
              {NAV.map((link, i) => (
                <li key={link.label}>
                  <button
                    data-testid={link.testid}
                    onClick={() =>
                      link.href === "#top" ? window.scrollTo({ top: 0, behavior: "smooth" }) : scrollToId(link.href)
                    }
                    className="text-sm text-bone/80 transition-colors duration-300 hover:text-mint"
                  >
                    {t(navKeys[i])}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-fog">{t("footer.contact")}</p>
            <div className="mt-5 space-y-4">
              {CONTACTS.map((c) => (
                <a
                  key={c.tel}
                  data-testid={`footer-phone-link-${c.testid}`}
                  href={`tel:${c.tel}`}
                  className="flex items-center gap-3 text-sm text-bone/80 transition-colors hover:text-mint"
                >
                  <Phone className="h-4 w-4 shrink-0 text-mint" />
                  <span>
                    {c.phone} <span className="text-fog">· {c.name}</span>
                  </span>
                </a>
              ))}
              <a
                data-testid="footer-email-link"
                href="mailto:nivadigitals@gmail.com"
                className="flex items-center gap-3 text-sm text-bone/80 transition-colors hover:text-mint"
              >
                <Mail className="h-4 w-4 text-mint" /> nivadigitals@gmail.com
              </a>
              <p className="text-sm text-fog">{t("footer.location")}</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 py-8 sm:flex-row">
          <p className="text-xs text-fog">{t("footer.rights")}</p>
          <a
            data-testid="footer-privacy-link"
            href="/integritetspolicy"
            className="text-xs text-fog transition-colors hover:text-mint"
          >
            {t("footer.privacy")}
          </a>
        </div>
      </div>
    </footer>
  );
}
